import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fires the `my-items` fetch the instant we have a signed-in user, so the
 * Inventory page (which is lazy-loaded) doesn't have to serialize
 * bundle-load → mount → fetch. On cellular this shaves a noticeable
 * chunk off perceived load time. React Query dedupes with the eventual
 * useMyItems() mount, so this is safe.
 *
 * Keep the column list in sync with useMyItems' MY_ITEMS_COLUMNS.
 */
const MY_ITEMS_COLUMNS = [
  'id', 'short_id', 'user_id', 'title', 'category', 'condition',
  'game', 'custom_game_name', 'language',
  'set_code', 'set_name', 'era', 'product_type',
  'visibility', 'is_beta', 'quantity',
  'asking_price_cents', 'previous_price_cents', 'price_dropped_at',
  'market_price_cents', 'market_price_confidence', 'market_price_source',
  'market_price_fetched_at', 'market_price_low_cents', 'market_price_high_cents',
  'collectr_market_price_cents', 'collectr_price_override_cents',
  'cost_basis_cents', 'individual_costs_cents',
  'stock_image_url', 'sold_at', 'listed_at', 'created_at', 'updated_at',
  'grading_company', 'slab_grade', 'certification_number',
  'is_negotiable', 'accepts_trades', 'has_proof_pack', 'proof_images',
  'card_number', 'finish', 'finish_label', 'rarity', 'pokemon_type', 'artist',
  'is_first_edition', 'is_reverse_holo', 'is_error_card',
  'sealed_condition', 'pack_count',
  'scrydex_match_confidence', 'scrydex_matched_at',
  'ebay_title_override',
  'city', 'state_code', 'ships_anywhere',
  'description', 'notes', 'purchase_date', 'target_price_cents', 'error_type',
  'lot_type', 'approx_card_count', 'box_count', 'lot_highlights',
  'lot_eras', 'lot_languages',
].join(', ');

export function useInventoryWarmup() {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;
    qc.prefetchQuery({
      queryKey: ['my-items', user.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('user_items')
          .select(`${MY_ITEMS_COLUMNS}, images:user_item_images(*)`)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data || []).map((item: any) => ({
          ...item,
          images:
            item.images?.sort((a: any, b: any) => a.display_order - b.display_order) || [],
        }));
      },
      staleTime: 60 * 1000,
    });
  }, [user?.id, qc]);
}
